/**
 * What a delivery is still waiting on, as small badges.
 *
 * Done, earned, paid and checked are separate facts (see
 * DELIVERY_PENDING_VERIFICATION), so each gets its own badge rather than one
 * status pretending to answer all four. Nothing is shown for a record made
 * before these controls existed: it was never asked, and a badge would claim
 * otherwise.
 */

import type { ReactNode } from 'react';
import type { Delivery } from '@cenox/shared';
import { Badge } from '../../components/ui';

type Controls = Pick<
  Delivery,
  'feeStatus' | 'paymentVerification' | 'revenueState' | 'pendingApprovals' | 'paymentStatus'
>;

export function ControlBadges({ delivery: d }: { delivery: Partial<Controls> }): ReactNode {
  const badges: ReactNode[] = [];

  /*
    Superseded, by that word.

    These rows appear in History only — no operational card counts them, since
    the same job is counted once under its canonical record. The label has to
    say which of the two this is, or the pair looks like double counting rather
    than a duplicate deliberately kept.
  */
  if (d.revenueState === 'superseded') {
    badges.push(<Badge key="dup">Superseded · kept for audit</Badge>);
  }

  if (d.feeStatus === 'exception_pending') {
    badges.push(<Badge key="fee" tone="warning">Fee awaiting approval</Badge>);
  } else if (d.feeStatus === 'exception_approved') {
    badges.push(<Badge key="fee" tone="info">Fee approved as exception</Badge>);
  } else if (d.feeStatus === 'exception_rejected') {
    badges.push(<Badge key="fee" tone="danger">Fee rejected</Badge>);
  }

  if (d.pendingApprovals?.includes('backdate')) {
    badges.push(<Badge key="date" tone="warning">Date awaiting approval</Badge>);
  }

  if (d.paymentVerification === 'pending') {
    badges.push(<Badge key="pay" tone="warning">Payment pending verification</Badge>);
  } else if (d.paymentVerification === 'verified') {
    badges.push(<Badge key="pay" tone="success">Payment verified</Badge>);
  } else if (d.paymentVerification === 'rider_held') {
    badges.push(<Badge key="pay">Rider holds cash</Badge>);
  } else if (d.paymentVerification === 'rejected') {
    badges.push(<Badge key="pay" tone="danger">Payment rejected</Badge>);
  }

  // Done at an authorised price but not paid for: owed, not lost.
  if (d.revenueState === 'earned' && d.paymentStatus !== 'Paid') {
    badges.push(<Badge key="earned" tone="info">Earned · unpaid</Badge>);
  }

  return badges.length ? <>{badges}</> : null;
}
